"use client";

import React, { useState, useCallback, useRef } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useOpenerFocus } from "@/lib/a11y/useOpenerFocus";
import { ensurePortalHost } from "@/design-system/primitives/ThemedPortal";

export interface ConfirmDialogOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
  /** Existing native-confirm workflows may keep Enter = confirm; other callers stay Cancel-first. */
  initialFocus?: "cancel" | "confirm";
}

interface DialogState extends ConfirmDialogOptions {
  open: boolean;
}

type Resolver = (value: boolean) => void;

let showDialog: ((options: ConfirmDialogOptions) => Promise<boolean>) | null =
  null;

/** Calls waiting for a provider to register (the on-demand one below). */
const waiters: Array<() => void> = [];
let onDemandMounted = false;

/**
 * No provider mounted (a page outside the root layout, a test): mount the SAME provider on demand, once, in its own
 * stable host. There is one confirm (docs/THEME.md 2.9): never the browser's own confirm box (std-overlays G00).
 */
function mountOnDemandProvider(): Promise<void> {
  if (!onDemandMounted) {
    onDemandMounted = true;
    createRoot(ensurePortalHost(document, { id: "confirm-dialog-root" })).render(<ConfirmDialogProvider />);
  }
  return new Promise<void>((resolve) => {
    if (showDialog) resolve();
    else waiters.push(resolve);
  });
}

/**
 * Imperative confirm dialog — call from anywhere, no hooks needed.
 *
 * Usage:
 *   import { confirmDialog } from "@/lib/dialogs";
 *   if (await confirmDialog({ title: "Delete?", description: "This cannot be undone." })) { ... }
 */
export async function confirmDialog(
  options: ConfirmDialogOptions
): Promise<boolean> {
  if (!showDialog) {
    // Server render: there is nothing to ask, and nothing is confirmed.
    if (typeof document === "undefined") return false;
    await mountOnDemandProvider();
  }
  return showDialog!(options);
}

/**
 * Mount once in layout.tsx — renders the shared dialog instance.
 */
export function ConfirmDialogProvider() {
  const [state, setState] = useState<DialogState>({
    open: false,
    title: "",
    description: "",
  });
  const resolverRef = useRef<Resolver | null>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const { onCloseAutoFocus } = useOpenerFocus(state.open);

  const show = useCallback(
    (options: ConfirmDialogOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setState({ ...options, open: true });
      });
    },
    []
  );

  // Register the singleton
  React.useEffect(() => {
    showDialog = show;
    waiters.splice(0).forEach((resolve) => resolve());
    return () => {
      // Only clear our own registration: another provider may have registered since.
      if (showDialog === show) showDialog = null;
    };
  }, [show]);

  const close = useCallback((result: boolean) => {
    setState((prev) => ({ ...prev, open: false }));
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  // Before the std-overlays foundation this was a hand-made DialogPrimitive at z-[100200] / z-[100201] (named here so the
  // compiled CSS keeps those utilities: other code may still use them; the CSS-additive screenshot check holds that).
  // The one confirm (docs/THEME.md 2.9): the shared AlertDialog. minDepth 3 keeps it where it always was, above
  // nested dialogs (z 100200 / 100201), and it still goes higher when deeper modals are open. An outside press cancels,
  // as it did before the move.
  return (
    <AlertDialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) close(false);
      }}
    >
      <AlertDialogContent minDepth={3} onCloseAutoFocus={onCloseAutoFocus} onPointerDownOutside={() => close(false)}
        onOpenAutoFocus={(event) => {
          if (state.initialFocus !== "confirm") return;
          event.preventDefault();
          actionRef.current?.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{state.title}</AlertDialogTitle>
          <AlertDialogDescription>{state.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {state.cancelText || "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction
            ref={actionRef}
            tone={state.variant === "destructive" ? "danger" : "default"}
            onClick={() => close(true)}
          >
            {state.confirmText || "Confirm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
